<template>
    <div class="theme-merchant">
        <SmuToast :toasts="toasts" />
        <SmuSite :sidebar-shown="sidebarOpen">
            <template #header>
                <SmuHeader
                    ref="headerRef"
                    :title="appName"
                    :width
                    :subtitle="$t('frontend.merchant_administration')"
                >
                    <template #sidebar-toggle>
                        <SmuShowSidebarToggle v-model="sidebarOpen" />
                    </template>
                    <template #center>
                        <PageHeaderEditState v-if="hasChanges" />
                        <Breadcrumbs v-if="!hasChanges && isNormalPage" />
                    </template>
                    <template #right-side>
                        <VectorizeQueueButton v-if="user && !hasChanges" />
                        <div v-if="!hasChanges" class="relative">
                            <LanguageDropdown></LanguageDropdown>
                        </div>
                        <div
                            v-if="user && !hasChanges"
                            data-testid="header-user-menu"
                        >
                            <SmuUserMenu :user="user" class="px-0!">
                                <SmuUserMenuItem
                                    data-testid="header-user-menu-change-password"
                                    icon="pencil"
                                >
                                    {{ $t("helpers.buttons.change_password") }}
                                </SmuUserMenuItem>
                                <SmuUserMenuItem
                                    data-testid="header-user-menu-logout"
                                    icon="arrow-right-from-bracket"
                                    @click="logout"
                                >
                                    {{ $t("helpers.buttons.logout") }}
                                </SmuUserMenuItem>
                            </SmuUserMenu>
                        </div>
                    </template>
                    <span>{{ route.meta.title }}</span>
                </SmuHeader>
            </template>

            <template v-if="user" #menue>
                <div class="flex h-full flex-col justify-between">
                    <div>
                        <PageMainMenu />
                    </div>
                    <div>
                        <PageDefaultFooter />
                    </div>
                </div>
            </template>
            <slot />
        </SmuSite>
        <VectorizeQueueLiveDialog v-if="user" />
    </div>
</template>

<script setup lang="ts">
import { SmuHeader } from "@smakecloud/smake-ui";
import { aliasMap } from "~/lib/shortcuts";
import { trackEvent } from "~/utils/hotjarTracking";
import { resolveChosenLanguage } from "~/utils/localeResolver";

const { useToast } = await import("@smakecloud/smake-ui");

const { data } = storeToRefs(useUserStore());

import { useRoute as useUntypedRoute } from "vue-router";
const route = useRoute();
const untypedRoute = useUntypedRoute();
const sidebarStore = useSidebarStore();
const { sidebarOpen } = storeToRefs(sidebarStore);
const { syncInitialLocale, updateLocale } = useLocaleUpdate();
const tokenStore = useTokenStore();
const { token } = storeToRefs(tokenStore);
const { locale } = storeToRefs(useLocalSettingsStore());
const user = data;

const appName = computed(() => {
    return data.value?.app.display_name ?? "";
});

const { logout } = useTokenStore();

const { hasChanges } = useUnsavedChangesHeader();

const { toasts } = useToast();

useHead({
    meta: [{ content: "Smake Merchant Management", property: "og:title" }],
    titleTemplate: (title) =>
        title
            ? `${title} - Smake Merchant Management`
            : "Smake Merchant Management",
});

const isNormalPage = computed(() => {
    if (isGuestPage(untypedRoute) || is404Page(untypedRoute)) return false;

    return true;
});

const width = computed(() => route.meta.width);

const keys = useMagicKeys({
    aliasMap,
    passive: false,
});

whenever(keys.escape, () => {
    trackEvent("esc_shortcut");

    sidebarOpen.value = false;
});

watch(
    () => (token.value ? data.value : undefined),
    async (user) => {
        if (!user) return;
        await syncInitialLocale(user);
    },
);
watch(locale, async (code) => {
    const user = data.value;
    if (!user || !token.value) return;
    if (user.user_locale?.code === code) return;
    const language = resolveChosenLanguage(
        code,
        user.app.languages,
        user.user_locale ?? user.app.language_default,
    );
    await updateLocale(user.id, language);
});
</script>
